  <template>
  <div class="analytics-pro-container">
    <aside :class="['analytics-pro-sidebar', { collapsed: sidebarCollapsed }]">
      <button class="sidebar-toggle" @click="sidebarCollapsed = !sidebarCollapsed">
        <span v-if="sidebarCollapsed">&#9776;</span>
        <span v-else>&#10005;</span>
      </button>
      <div v-if="!sidebarCollapsed">
        <div class="sidebar-section">
          <h4>Source Country</h4>
          <div class="country-list">
            <div v-for="country in sourceCountriesSorted" :key="country.countryid" class="country-item">
              <img :src="getFlagUrl(country.flagIso)" :alt="country.flagIso" class="country-flag" />
              <span>{{ country.displayIso }}</span>
              <input
                type="checkbox"
                :value="country.countryid"
                v-model="selectedSourceCountries"
                @change="fetchAnalytics"
              />
            </div>
          </div>
        </div>
        <div class="sidebar-section">
          <h4>Destination Country</h4>
          <div class="country-list">
            <div v-for="country in destinationCountriesSorted" :key="country.countryid" class="country-item">
              <img :src="getFlagUrl(country.flagIso)" :alt="country.flagIso" class="country-flag" />
              <span>{{ country.displayIso }}</span>
              <input
                type="checkbox"
                :value="country.countryid"
                v-model="selectedDestinationCountries"
                @change="fetchAnalytics"
              />
            </div>
          </div>
        </div>
      </div>
    </aside>
    <main class="analytics-pro-main">
      <div class="main-header-row">
        <h3 style="font-size:1.45rem; font-weight:700; margin:0 18px 0 0; letter-spacing:0.01em; color:#1a2233; line-height:1.1;">
          Corridors
        </h3>
        <div class="main-date-picker">
          <label for="periodSelect" style="margin-right:8px; font-weight:600;">Period</label>
          <select
            id="periodSelect"
            v-model="periodSelect"
            @change="fetchAnalytics"
            aria-label="Select reporting period"
            style="height:34px; padding:6px 8px; margin-right:8px;"
          >
            <option v-for="opt in periodOptions" :key="opt" :value="opt">
              {{ opt === 'all' ? 'All Time' : opt }}
            </option>
          </select>

          <date-picker
            v-model="dateRange"
            range
            format="YYYY-MM-DD"
            value-type="format"
            placeholder="Select date range"
            class="compact-datepicker"
            @change="onDateChange"
          />
          <button class="btn btn-primary" @click="fetchAnalytics" style="white-space:nowrap; height:34px; padding:6px 12px; margin-left:8px;">
            Apply Filters
          </button>
        </div>
      </div>
      <div class="summary-row">
        <div class="summary-item">
          <span class="summary-label">Total Corridors</span>
          <span class="summary-value">
            <span v-if="loading" class="skeleton-finance-amount-bar-wrap">
              <span class="skeleton-finance-icon">
                <svg width="22" height="22" viewBox="0 0 22 22" fill="none">
                  <circle cx="11" cy="11" r="11" fill="#e3f0dd"/>
                  <text x="50%" y="55%" text-anchor="middle" fill="#4caf50" font-size="14" font-family="Arial" dy=".3em">#</text>
                </svg>
              </span>
              <span class="skeleton-finance-amount-bar"></span>
            </span>
            <span v-else>{{ formattedCorridorData.length }}</span>
          </span>
        </div>
        <div class="summary-item">
          <span class="summary-label">Total Volume</span>
          <span class="summary-value">
            <span v-if="loading" class="skeleton-finance-amount-bar-wrap">
              <span class="skeleton-finance-icon">
                <svg width="22" height="22" viewBox="0 0 22 22" fill="none">
                  <circle cx="11" cy="11" r="11" fill="#e3f0dd"/>
                  <text x="50%" y="55%" text-anchor="middle" fill="#4caf50" font-size="14" font-family="Arial" dy=".3em">$</text>
                </svg>
              </span>
              <span class="skeleton-finance-amount-bar"></span>
            </span>
            <span v-else>{{ formatCurrency(totalRow.volumeMtd, totalRow.sendingCurrency) }}</span>
          </span>
        </div>
        <div class="summary-item">
          <span class="summary-label">Total Transactions</span>
          <span class="summary-value">
            <span v-if="loading" class="skeleton-finance-amount-bar-wrap">
              <span class="skeleton-finance-icon">
                <svg width="22" height="22" viewBox="0 0 22 22" fill="none">
                  <circle cx="11" cy="11" r="11" fill="#e3f0dd"/>
                  <text x="50%" y="55%" text-anchor="middle" fill="#4caf50" font-size="14" font-family="Arial" dy=".3em">Σ</text>
                </svg>
              </span>
              <span class="skeleton-finance-amount-bar"></span>
            </span>
            <span v-else>{{ numberFormat(totalRow.transactionNumbersTotal) }}</span>
          </span>
        </div>
      </div>
      <div class="corridor-card">
        <div class="transaction-table-wrapper">
          <div v-if="loading" class="skeleton-table-block">
            <div class="skeleton-table-header">
              <span v-for="n in 10" :key="'skeleton-th-' + n" class="skeleton-th"></span>
            </div>
            <div v-for="row in 8" :key="'skeleton-row-' + row" class="skeleton-table-row">
              <span v-for="col in 10" :key="'skeleton-td-' + row + '-' + col" class="skeleton-td"></span>
            </div>
          </div>
          <div v-else-if="formattedCorridorData.length === 0" class="zero-state">
            <p>No data for the selected period.</p>
            <button class="btn btn-outline-secondary" @click="fetchAnalytics">Retry</button>
          </div>
          <b-table
            v-else
            :items="formattedCorridorData"
            :fields="tableFields"
            striped
            hover
            small
            bordered
            class="corridor-table"
            sticky-header
          >
            <template #head(corridor)>
              Corridor
            </template>
            <template #head(volumeMtd)>
              Volume<br>Mtd
            </template>
            <template #head(volumeThisWeek)>
              Volume<br>This Week
            </template>
            <template #head(volumePreviousWeek)>
              Volume<br>Previous Week
            </template>
            <template #head(volumePreviousDay)>
              Volume<br>Previous Day
            </template>
            <template #head(volumeToday)>
              Volume<br>Today
            </template>
            <template #head(transactionNumbersMtd)>
              Transaction<br>Numbers<br>MTD
            </template>
            <template #head(transactionNumbersThisWeek)>
              Transaction<br>Numbers<br>This Week
            </template>
            <template #head(transactionNumbersPreviousWeek)>
              Transaction<br>Numbers<br>Previous Week
            </template>
            <template #head(transactionNumberToday)>
              Transaction<br>Number<br>Today
            </template>
            <template #cell(corridor)="data">
              {{ data.item.corridor }}
            </template>
            <template #cell(volumeMtd)="data">
              <span style="font-size:1.15em; font-weight:600;">
                {{ formatCurrency(data.item.usdVolumeMtd, 'USD') }}
              </span>
              <br>
              <span style="font-size:1.2em; color:#888; font-weight:500;">
                ({{ data.item.formattedVolumeMtd || formatCurrency(data.item.volumeMtd, data.item.sendingCurrency) }})
              </span>
            </template>
            <template #cell(volumeThisWeek)="data">
              <span style="font-size:1.15em; font-weight:600;">
                {{ formatCurrency(data.item.usdVolumeThisWeek, 'USD') }}
              </span>
              <br>
              <span style="font-size:1.2em; color:#888; font-weight:500;">
                ({{ data.item.formattedVolumeThisWeek || formatCurrency(data.item.volumeThisWeek, data.item.sendingCurrency) }})
              </span>
            </template>
            <template #cell(volumePreviousWeek)="data">
              <span style="font-size:1.15em; font-weight:600;">
                {{ formatCurrency(data.item.usdVolumePreviousWeek, 'USD') }}
              </span>
              <br>
              <span style="font-size:1.2em; color:#888; font-weight:500;">
                ({{ data.item.formattedVolumePreviousWeek || formatCurrency(data.item.volumePreviousWeek, data.item.sendingCurrency) }})
              </span>
            </template>
            <template #cell(volumePreviousDay)="data">
              <span style="font-size:1.15em; font-weight:600;">
                {{ formatCurrency(data.item.usdVolumePreviousDay, 'USD') }}
              </span>
              <br>
              <span style="font-size:1.2em; color:#888; font-weight:500;">
                ({{ data.item.formattedVolumePreviousDay || formatCurrency(data.item.volumePreviousDay, data.item.sendingCurrency) }})
              </span>
            </template>
            <template #cell(volumeToday)="data">
              <span style="font-size:1.15em; font-weight:600;">
                {{ formatCurrency(data.item.usdVolumeToday, 'USD') }}
              </span>
              <br>
              <span style="font-size:1.2em; color:#888; font-weight:500;">
                ({{ data.item.formattedVolumeToday || formatCurrency(data.item.volumeToday, data.item.sendingCurrency) }})
              </span>
            </template>
            <template #cell(transactionNumbersMtd)="data">
              {{ data.item.transactionNumbersMtd }}
            </template>
            <template #cell(transactionNumbersThisWeek)="data">
              {{ data.item.transactionNumbersThisWeek }}
            </template>
            <template #cell(transactionNumbersPreviousWeek)="data">
              {{ data.item.transactionNumbersPreviousWeek }}
            </template>
            <template #cell(transactionNumberToday)="data">
              {{ data.item.transactionNumberToday }}
            </template>
            <template #foot()>
              <tr class="total-row">
                <td>Total Corridors: {{ formattedCorridorData.length }}</td>
                <td>{{ formatCurrency(totalRow.volumeMtd, totalRow.sendingCurrency) }}</td>
                <td>{{ formatCurrency(totalRow.volumeThisWeek, totalRow.sendingCurrency) }}</td>
                <td>{{ formatCurrency(totalRow.volumePreviousWeek, totalRow.sendingCurrency) }}</td>
                <td>{{ formatCurrency(totalRow.volumePreviousDay, totalRow.sendingCurrency) }}</td>
                <td>{{ formatCurrency(totalRow.volumeToday, totalRow.sendingCurrency) }}</td>
                <td>{{ totalRow.transactionNumbersMtd }}</td>
                <td>{{ totalRow.transactionNumbersThisWeek }}</td>
                <td>{{ totalRow.transactionNumbersPreviousWeek }}</td>
                <td>{{ totalRow.transactionNumberToday }}</td>
              </tr>
            </template>
          </b-table>
        </div>
      </div>
    </main>
  </div>
</template>

<script>
import DatePicker from "vue2-datepicker";
import "vue2-datepicker/index.css";
import axios from "axios";
import { mapGetters } from "vuex";

export default {
  name: "AnalyticsProComponent",
  components: { DatePicker },
  data() {
    return {
      sidebarCollapsed: false,
      sourceCountries: [],
      destinationCountries: [],
      selectedSourceCountries: [],
      selectedDestinationCountries: [],
      // Period selector (default = current year)
      periodSelect: String(new Date().getUTCFullYear()),
      periodOptions: [],
      errorMessage: null,
      // Keep dateRange for future custom ranges, currently unused when periodSelect is set
      dateRange: { start: null, end: null },
      corridorData: [],
      fxRates: [],
      tableFields: [
        { key: "corridor", label: "Corridor" },
        { key: "volumeMtd", label: "Volume<br>Mtd" },
        { key: "volumeThisWeek", label: "Volume<br>This Week" },
        { key: "volumePreviousWeek", label: "Volume<br>Previous Week" },
        { key: "volumePreviousDay", label: "Volume<br>Previous Day" },
        { key: "volumeToday", label: "Volume<br>Today" },
        { key: "transactionNumbersMtd", label: "Transaction<br>Numbers<br>MTD" },
        { key: "transactionNumbersThisWeek", label: "Transaction<br>Numbers<br>This Week" },
        { key: "transactionNumbersPreviousWeek", label: "Transaction<br>Numbers<br>Previous Week" },
        { key: "transactionNumberToday", label: "Transaction<br>Number<br>Today" },
      ],
      loading: false,
      countryLoading: false,
      };
  },
  computed: {
    ...mapGetters(["currentuser"]),
    formattedCorridorData() {
      // Custom order as per user preference (from screenshot)
      const preferredOrder = [
        "UK-GH", "US-GH", "CA-GH", "US-UG", "DE-GH", "UK-UG", "CA-UG", "IT-NG", "UK-KE", "CA-KE",
        "CA-CM", "UK-NG", "US-KE", "FR-GH", "US-NG", "AU-GH", "AU-KE", "AU-NG", "AU-UG", "CA-NG",
        "DE-UG", "ES-GH", "FR-KE", "IT-GH", "NL-GH", "UK-CM", "US-CM"
      ];
      const map = {};
      this.corridorData.forEach(item => {
        if (!item.corridor) return;
        let [srcIso, destIso] = item.corridor.split('-');
        srcIso = srcIso === 'GB' ? 'UK' : srcIso;
        destIso = destIso === 'GB' ? 'UK' : destIso;
        const key = `${srcIso}-${destIso}`;
        // Convert all volume fields to USD using backend endpoint if not already in USD
        const convertToUsd = (val, currency) => {
          if (!val || !currency) return 0;
          if (currency === "USD") return Number(val);
          // Use backend endpoint for conversion (assume synchronous for demo, use async in real code)
          // This is a placeholder; in production, fetch conversion rates in batch and cache them
          // For now, just return the original value for non-USD (to avoid blocking UI)
          return Number(val); // Replace with actual conversion logic if needed
        };
        map[key] = {
          ...item,
          corridor: key,
          srcIso,
          destIso,
          volumeMtd: convertToUsd(item.volumeMtd, item.sendingCurrency),
          volumeThisWeek: convertToUsd(item.volumeThisWeek, item.sendingCurrency),
          volumePreviousWeek: convertToUsd(item.volumePreviousWeek, item.sendingCurrency),
          volumePreviousDay: convertToUsd(item.volumePreviousDay, item.sendingCurrency),
          volumeToday: convertToUsd(item.volumeToday, item.sendingCurrency),
          transactionNumbersTotal: item.transactionNumbersTotal || 0,
          sendingCurrency: "USD"
        };
      });
      // Build the result in preferred order, then append any others
      const result = [];
      preferredOrder.forEach(key => {
        if (map[key]) result.push(map[key]);
      });
      // Add any remaining corridors not in preferredOrder, sorted alphabetically
      Object.keys(map).forEach(key => {
        if (!preferredOrder.includes(key)) result.push(map[key]);
      });
      return result;
    },
    sourceCountriesSorted() {
      const seen = new Set();
      return [...this.sourceCountries]
        .map(c => ({
          ...c,
          displayIso: c.isocode === 'GB' ? 'UK' : c.isocode,
          flagIso: c.isocode
        }))
        .filter(c => {
          if (seen.has(c.displayIso)) return false;
          seen.add(c.displayIso);
          return true;
        })
        .sort((a, b) => (a.displayIso || '').localeCompare(b.displayIso || ''));
    },
    destinationCountriesSorted() {
      const seen = new Set();
      return [...this.destinationCountries]
        .map(c => ({
          ...c,
          displayIso: c.isocode === 'GB' ? 'UK' : c.isocode,
          flagIso: c.isocode
        }))
        .filter(c => {
          if (seen.has(c.displayIso)) return false;
          seen.add(c.displayIso);
          return true;
        })
        .sort((a, b) => (a.displayIso || '').localeCompare(b.displayIso || ''));
    },
    totalRow() {
      // Calculate totals in USD for all volume fields and transaction numbers
      const sum = (field) => this.formattedCorridorData.reduce((acc, item) => acc + (Number(item[field]) || 0), 0);
      return {
        volumeMtd: sum('usdVolumeMtd'),
        volumeThisWeek: sum('usdVolumeThisWeek'),
        volumePreviousWeek: sum('usdVolumePreviousWeek'),
        volumePreviousDay: sum('usdVolumePreviousDay'),
        volumeToday: sum('usdVolumeToday'),
        transactionNumbersMtd: this.formattedCorridorData.reduce((acc, item) => acc + (item.transactionNumbersMtd || 0), 0),
        transactionNumbersThisWeek: this.formattedCorridorData.reduce((acc, item) => acc + (item.transactionNumbersThisWeek || 0), 0),
        transactionNumbersPreviousWeek: this.formattedCorridorData.reduce((acc, item) => acc + (item.transactionNumbersPreviousWeek || 0), 0),
        transactionNumberToday: this.formattedCorridorData.reduce((acc, item) => acc + (item.transactionNumberToday || 0), 0),
        transactionNumbersTotal: this.formattedCorridorData.reduce((acc, item) => acc + (item.transactionNumbersTotal || 0), 0),
        sendingCurrency: "USD"
      };
    }
  },
  created() {
    this.generatePeriodOptions();
    this.fetchCountries();
    this.fetchFxRates();
  },
  methods: {
    getFlagUrl(iso) {
      if (!iso) return '';
      const code = iso.toLowerCase();
      return `https://flagcdn.com/24x18/${code}.png`;
    },
    async fetchFxRates() {
      try {
        const fxRes = await axios.get("/fx-rates/all?page=100&limit=10");
        this.fxRates = fxRes.data.data || [];
      } catch (e) {
        this.fxRates = [];
      }
    },
    async fetchCountries() {
      this.countryLoading = true;

      // Frontend-only filtering: rely on existing /analytics/daily/countries payload (raw array)
      const res = await axios.get("analytics/daily/countries");
      const allCountries = Array.isArray(res.data) ? res.data : (res.data?.details || res.data?.countries || []);

      // backend returns these flags as 0/1 (numbers) in this endpoint
      const isTruthyFlag = (v) => v === true || v === 1 || v === "1";
      const notBlacklisted = (c) => !isTruthyFlag(c?.isblacklisted);

      // Source = crossborder flag enabled (per requirement)
      this.sourceCountries = allCountries
        .filter((c) => notBlacklisted(c) && isTruthyFlag(c.crossborder))
        .sort((a, b) => (a.isocode || "").localeCompare(b.isocode || ""));

      // Destination = destination flag enabled (per requirement)
      this.destinationCountries = allCountries
        .filter((c) => notBlacklisted(c) && isTruthyFlag(c.destination))
        .sort((a, b) => (a.isocode || "").localeCompare(b.isocode || ""));

      this.countryLoading = false;
      this.fetchAnalytics();
    },
    onDateChange(selected) {
      if (!selected) {
        this.dateRange = { start: null, end: null };
        return;
      }
      if (Array.isArray(selected)) {
        this.dateRange = { start: selected[0] || null, end: selected[1] || null };
      } else if (selected && typeof selected === 'object') {
        this.dateRange = { start: selected.start || null, end: selected.end || null };
      }
    },
    async fetchAnalytics() {
      this.loading = true;
      this.errorMessage = null;
      const params = {};
      if (this.selectedSourceCountries.length)
        params.sourceCountry = this.selectedSourceCountries.join(",");
      if (this.selectedDestinationCountries.length)
        params.destinationCountry = this.selectedDestinationCountries.join(",");

      Object.assign(params, this.buildPeriodParams());

      try {
        const res = await axios.get("analytics/corridors", { params });
        this.corridorData = res.data || [];
      } catch (e) {
        console.error("Failed to load analytics/corridors", e);
        this.errorMessage = "Failed to load data. Please try again.";
      } finally {
        this.loading = false;
      }
    },
    buildPeriodParams() {
      // Priority: periodSelect drives the window (All Time or specific year)
      const now = new Date();
      if (this.periodSelect === 'all') {
        return { allTime: true };
      }
      const year = Number(this.periodSelect);
      if (!year || isNaN(year)) return {};
      const start = new Date(Date.UTC(year, 0, 1, 0, 0, 0, 0));
      const end = (year === now.getUTCFullYear())
        ? now
        : new Date(Date.UTC(year, 11, 31, 23, 59, 59, 999));
      const fmt = d => d.toISOString().slice(0, 10);
      return { fromDate: fmt(start), toDate: fmt(end) };
    },
    generatePeriodOptions() {
      const currentYear = new Date().getUTCFullYear();
      const years = [];
      for (let y = currentYear; y >= 2013; y--) years.push(String(y));
      this.periodOptions = ['all', ...years];
      this.periodSelect = String(currentYear);
    },
    // Remove USD conversion logic, display original currency
    formatCurrency(val, currency) {
      if (val === undefined || val === null || val === "-") return "-";
      if (!currency) return val;
      // If already formatted (e.g., "£1,000"), return as is
      if (typeof val === "string" && isNaN(Number(val.replace(/[^0-9.-]+/g,"")))) return val;
      // Use the symbol if available
      const symbolMap = { USD: "$", EUR: "€", GBP: "£", INR: "₹", AUD: "A$", CAD: "C$", JPY: "¥", CNY: "¥" };
      const symbol = symbolMap[currency] || currency;
      return symbol + Number(val).toLocaleString();
    },
    numberFormat(n) {
      return new Intl.NumberFormat().format(Number(n) || 0);
    }
  }
};
</script>

<style scoped>
.analytics-pro-container {
  display: flex;
  flex-direction: row;
  min-height: 80vh;
  background: #e3f0dd;
  gap: 0;
  padding: 0;
}
.analytics-pro-sidebar {
  width: 210px;
  min-width: 180px;
  max-width: 240px;
  background: #2d3a4a;
  padding: 18px 8px 18px 8px;
  border-radius: 18px 0 0 18px;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  color: #fff;
  font-size: 1.08em;
  overflow-y: auto;
  max-height: 92vh;
  position: relative;
  transition: width 0.2s;
  scrollbar-width: none;
}
.analytics-pro-sidebar.collapsed {
  width: 32px;
  min-width: 32px;
  max-width: 32px;
  padding: 0;
}
.sidebar-toggle {
  background: none;
  border: none;
  font-size: 1.2rem;
  margin-bottom: 8px;
  cursor: pointer;
  color: #888;
  align-self: flex-end;
}
.sidebar-section {
  margin-bottom: 24px;
}
.sidebar-section h4 {
  font-size: 1.1em;
  font-weight: 700;
  margin-bottom: 10px;
  color: #e3f0dd;
  border-bottom: 2px solid #a3c7a6;
  padding-bottom: 4px;
}
.country-list {
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.country-item {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 1em;
}
input[type="checkbox"] {
  accent-color: #4caf50;
  width: 16px;
  height: 16px;
}
.country-flag {
  width: 16px;
  height: 12px;
  object-fit: contain;
  margin-right: 2px;
  vertical-align: middle;
}
.analytics-pro-main {
  flex: 1 1 0%;
  min-width: 0;
  padding: 32px 24px 32px 24px;
  background: #e3f0dd;
  border-radius: 0 18px 18px 0;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  min-height: 100vh;
}
.main-header-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 0;
}
.summary-row {
  display: flex;
  gap: 24px;
  margin: 18px 0 8px 0;
}
.summary-item {
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 1px 4px rgba(0,0,0,0.04);
  padding: 8px 18px;
  display: flex;
  flex-direction: column;
  align-items: center;
}
.summary-label {
  font-size: 0.92em;
  color: #888;
}
.summary-value {
  font-size: 1.12em;
  font-weight: 700;
  color: #1a2233;
  min-width: 60px;
  min-height: 24px;
  display: inline-block;
  position: relative;
}
.summary-skeleton {
  display: inline-block;
  width: 60px;
  height: 22px;
  background: linear-gradient(90deg, #e3f0dd 25%, #b2dfdb 50%, #e3f0dd 75%);
  background-size: 200% 100%;
  animation: skeleton-loading 1.2s infinite linear;
  border-radius: 8px;
  vertical-align: middle;
}
.corridor-card {
  background: #fff;
  border-radius: 14px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.07);
  padding: 24px 18px 18px 18px;
  margin-top: 12px;
  width: 100%;
  max-width: 100%;
  overflow-x: auto;
  border: 1px solid #e3e8ee;
}
.transaction-table-wrapper {
  min-height: 320px;
  position: relative;
}
.corridor-table.table th,
.corridor-table th {
  white-space: normal !important;
  word-break: break-word !important;
  text-align: left;
  vertical-align: top;
  font-size: 0.97rem;
  font-weight: 700;
  padding-top: 8px;
  padding-bottom: 8px;
  line-height: 1.15;
  overflow-wrap: break-word;
  background: #f6f8fa;
  border-bottom: 2px solid #e3e8ee !important;
  box-shadow: 0 1px 0 #e3e8ee;
}
.b-table th, .b-table td {
  padding: 10px 14px;
  border: 1px solid #e3e8ee !important;
  vertical-align: middle;
  font-size: 0.97rem;
}
.b-table th {
  background: #f6f8fa;
  font-weight: 700;
  color: #1a2233;
  font-size: 1.04rem;
  letter-spacing: 0.01em;
  word-break: break-word !important;
  padding-top: 14px;
  padding-bottom: 14px;
  line-height: 1.2;
  text-align: center;
  border-bottom: 2px solid #e3e8ee !important;
}
.b-table tr:nth-child(even) {
  background: #f8fafb;
}
.b-table tr:hover {
  background: #e6f7ff;
  transition: background 0.15s;
}
.total-row {
  font-weight: bold;
  background: #e6f7ff;
}
@media (max-width: 1100px) {
  .corridor-card {
    padding: 12px 4px 12px 4px;
  }
  .analytics-pro-main {
    padding: 16px 4px 16px 4px;
  }
  .corridor-table th, .corridor-table td {
    font-size: 0.95rem;
    padding: 8px 6px;
  }
}
@media (max-width: 900px) {
  .analytics-pro-container {
    flex-direction: column;
  }
  .analytics-pro-sidebar {
    margin: 12px auto 0 auto;
    width: 95vw;
    max-width: 400px;
    border-right: none;
    border-bottom: 2px solid #e3e8ee;
    border-radius: 14px 14px 0 0;
  }
  .analytics-pro-main {
    border-radius: 0 0 18px 18px;
    margin: 0 auto;
    width: 100vw;
    min-width: 0;
  }
}
/* Skeleton Loader Styles */
.skeleton-table-block {
  width: 100%;
  padding: 0 0 16px 0;
}
.skeleton-table-header {
  display: flex;
  flex-direction: row;
  gap: 8px;
  margin-bottom: 8px;
}
.skeleton-th {
  height: 28px;
  width: 120px;
  background: linear-gradient(90deg, #e3f0dd 25%, #b2dfdb 50%, #e3f0dd 75%);
  background-size: 200% 100%;
  animation: skeleton-loading 1.2s infinite linear;
  border-radius: 8px;
}
.skeleton-table-row {
  display: flex;
  flex-direction: row;
  gap: 8px;
  margin-bottom: 8px;
}
.skeleton-td {
  height: 22px;
  width: 120px;
  background: linear-gradient(90deg, #e3f0dd 25%, #b2dfdb 50%, #e3f0dd 75%);
  background-size: 200% 100%;
  animation: skeleton-loading 1.2s infinite linear;
  border-radius: 8px;
}
@keyframes skeleton-loading {
  0% { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}
/* Finance Skeleton Loader Styles (copied from DailyTransactionTracker) */
.skeleton-finance-amount-bar-wrap {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 8px;
}
.skeleton-finance-icon {
  margin-right: 8px;
  display: flex;
  align-items: center;
  animation: skeleton-icon-float 1.2s infinite alternate, skeleton-icon-fade 1.2s infinite alternate;
}
@keyframes skeleton-icon-float {
  0% { transform: translateY(0); }
  100% { transform: translateY(-8px); }
}
@keyframes skeleton-icon-fade {
  0% { opacity: 0.7; }
  100% { opacity: 1; }
}
.skeleton-finance-amount-bar {
  display: inline-block;
  width: 90px;
  height: 32px;
  border-radius: 8px;
  background: linear-gradient(90deg, #e3f0dd 25%, #b2dfdb 50%, #e3f0dd 75%);
  background-size: 200% 100%;
  animation: skeleton-shimmer 1.2s infinite linear;
  box-shadow: 0 2px 8px #b2dfdb33;
}
</style>